// src/lib/variables-essentielles.js — QUELLE VARIABLE VIDE A LE DROIT DE
// BLOQUER UN MESSAGE VOYAGEUR, ET LAQUELLE N'A QUE LE DROIT DE S'EFFACER.
//
// ⚠️ SOURCE UNIQUE (pas un miroir) : CJS pur (zéro import, zéro React, zéro
// Supabase), consommé côté SERVEUR par lib/messages-engine.js via le shim
// `lib/variables-essentielles.js` (re-export require) et côté CLIENT par
// l'éditeur de gabarits (garde « secondaire nue » AVANT le clic). Même
// doctrine que src/lib/sms-validator.js : CRA interdit l'import de la racine
// lib/ depuis src/, le require inverse est légal, et un miroir dupliqué serait
// un risque de drift sur un module de VALIDATION.
//
// ══ LE DÉFAUT (prod, 13/19/20 septembre 2026) ═══════════════════════════════
//
//   Le message d'accueil Airbnb `welcome_checkin_v2` de lc2 (adresse, codes
//   d'accès, Wi-Fi, parking, horaires, lien séjour) a été SAUTÉ trois fois :
//   `message_logs.status = 'skipped_missing_vars'`, `error_message =
//   'Variables manquantes : ville'`. La dernière ligne du gabarit dit « Nous
//   vous souhaitons un excellent séjour à {ville} 🌸 » ; lc2 n'a pas de ville
//   saisie ; le moteur a donc retenu TOUT le message — codes compris — pour
//   une fleur de politesse.
//
//   Règle de Dimitri (22/09) : « Une information secondaire ne devrait pas
//   empêcher un voyageur de recevoir ses codes. »
//
// ══ POURQUOI LA GARDE ÉTAIT SI LARGE ════════════════════════════════════════
//
//   `findMissingVars` (lib/template-variables.js) est née pour que JAMAIS un
//   message ne parte avec un trou à la place d'une donnée qui compte — un
//   Wi-Fi sans mot de passe, un rappel caution sans lien, un code promo vide.
//   Elle traite toute variable résolue à "" comme manquante, sans distinguer
//   « le lien de paiement » de « la ville ». Le contrat fail-closed est bon ;
//   son PÉRIMÈTRE était le défaut.
//
// ══ LA GARDE ÉTROITE ═══════════════════════════════════════════════════════
//
//   Deux listes FERMÉES, ici et nulle part ailleurs (le test
//   src/lib/variables-essentielles.test.js refuse toute variable du résolveur
//   ou du catalogue admin qui ne serait dans AUCUNE des deux) :
//
//   • ESSENTIELLE — vide ⇒ le message NE PART PAS (`skipped_missing_vars`,
//     inchangé). Ce sont les données pour lesquelles un blanc TROMPE ou
//     PRIVE le voyageur : codes d'accès et Wi-Fi, liens (séjour, paiement,
//     caution, formulaire, livret, avis, messagerie OTA), dates et heures,
//     nom et adresse du logement, montants d'argent, prénom/nom (le gabarit
//     s'adresse à la personne), contacts de la conciergerie, et TOUS les
//     drapeaux conditionnels (`is_airbnb`, `caution_manquante`…) : utilisés
//     inline hors {#if}, un drapeau vide signale un gabarit mal écrit —
//     contrat documenté dans template-variables.js, conservé tel quel.
//
//   • SECONDAIRE — vide ⇒ la variable S'EFFACE, le message PART, et le
//     journal porte `metadata.variables_vides = [...]` (noms de variables
//     seulement, jamais une valeur). Ce sont les informations de confort :
//     ville, étage, précision d'adresse, parking, poubelles, courses, règles
//     de la maison, nombre de personnes, langue, nom/logo FBZ…
//
//   Une variable INCONNUE des deux listes est traitée comme ESSENTIELLE
//   (fail-closed) : le résolveur la laisse en `{token}` littéral, et un
//   token littéral ne doit jamais partir.
//
// ══ L'EFFACEMENT ═══════════════════════════════════════════════════════════
//
//   `effacerVariablesSecondaires` retire les tokens secondaires vides du
//   GABARIT (avant rendu), et seulement eux — une clé essentielle passée par
//   erreur est ignorée (la liste fermée fait foi, jamais l'appelant) :
//     - une ligne qui ne contenait QUE ces tokens (plus ponctuation, émoji)
//       disparaît entière, avec son saut de ligne ;
//     - une virgule orpheline évidente (« a, {v}, b » → « a, b » ;
//       « a, {v} » → « a » ; « {v}, a » → « a ») est retirée ;
//     - un double espace créé par le retrait est ramené à un seul.
//   Une variable secondaire au MILIEU d'une phrase (« séjour à {ville} 🌸 »)
//   part sans sa valeur : « séjour à 🌸 ». C'est la copy qu'il faut alors
//   relire (entourer d'un {#if ville}…{/if}), pas le moteur qu'il faut
//   durcir. Quand aucune variable secondaire ne manque, la fonction rend le
//   gabarit OCTET-IDENTIQUE — prouvé par test.

// ── LISTE FERMÉE 1 — vide ⇒ l'envoi est retenu ─────────────────────────────
const VARIABLES_ESSENTIELLES = Object.freeze([
  // Le gabarit s'adresse à la personne : « Bonjour , » n'est pas un accueil.
  "guest_first_name",
  "guest_last_name",
  // Coordonnées citées dans un message = confirmation d'un contact ; un blanc
  // laisserait croire qu'aucun contact n'est enregistré.
  "guest_email",
  "guest_phone",
  // Dates et heures d'arrivée/départ (avec leurs alias) — un voyageur sans
  // son heure d'arrivée se présente à la mauvaise heure.
  "checkin_date",
  "checkout_date",
  "date_arrivee",
  "date_depart",
  "checkin_time",
  "checkout_time",
  "heure_arrivee",
  "heure_depart",
  // Argent : un montant vide dans un message est un montant faux.
  "total_amount",
  "revenue",
  "cleaning_fee",
  "caution_amount",
  "caution_status",
  "paiement_sejour_status",
  "paid_at_formatted",
  // Cadre juridique de la réservation — un blanc à la place de la politique
  // d'annulation trompe le voyageur sur ses droits.
  "cancellation_policy",
  // Référence de la demande directe : identifie ce dont on parle.
  "request_ref",
  // Nuits et source : dérivées des dates et de la plateforme, que toute résa
  // bien formée porte — vides, c'est la résa qui est cassée, et « Nuits : » /
  // « Source : » à blanc dans une notif admin trompent le lecteur.
  "nights",
  "source",
  "ota_source",
  // Nom et adresse du logement.
  "logement_name",
  "logement_full_name",
  "logement_address",
  "logement_address_full",
  // Codes d'accès, boîte à clés, Wi-Fi, instructions d'accès — LA raison
  // d'être d'un message d'arrivée.
  "access_building_instructions",
  "access_apartment_instructions",
  "door_code",
  "box_code",
  "interphone_code",
  "coffre_code",
  "garage_code",
  "wifi_name",
  "wifi_code",
  "wifi_instructions",
  // Liens : un lien vide est une promesse sans porte.
  "lien_sejour",
  "livret_url",
  "welcome_form_url",
  "checkin_form_url",
  "deposit_url",
  "caution_link",
  "paiement_caution_url",
  "paiement_sejour_link",
  "avis_google_url",
  "ota_chat_link",
  // Code de fidélité : un e-mail « -10 % » sans code reste une sollicitation
  // (LOT 3, garde spécifique conservée dans le moteur).
  "code_promo",
  // Contacts de la conciergerie : « Appelez-nous au » sans numéro, en cas de
  // souci à l'arrivée, laisse le voyageur seul. (Toujours résolus grâce aux
  // repli FBZ_DEFAULT_* — listés pour que l'intention soit lisible.)
  "fbz_phone",
  "telephone_contact",
  "fbz_email",
  // Drapeaux conditionnels — vides quand la condition est fausse ; utilisés
  // inline hors {#if}, ils rendent "true" ou rien : un gabarit qui les cite
  // en texte est mal écrit, l'envoi est retenu (contrat inchangé).
  "is_airbnb",
  "is_booking",
  "is_direct",
  "caution_required",
  "caution_required_effective",
  "caution_manquante",
  "caution_manquante_effective",
  "caution_empreinte",
  "caution_debit",
  "caution_received",
  "caution_authorized",
  "caution_captured",
  "payment_received",
  "deposit_link_visible",
  "welcome_checkin_allowed",
  "formulaire_manquant",
  "formulaire_rempli",
  "reservation_complete",
  "reservation_incomplete",
]);

// ── LISTE FERMÉE 2 — vide ⇒ la variable s'efface, l'envoi part ─────────────
const VARIABLES_SECONDAIRES = Object.freeze([
  // Localisation de confort — la ville qui a retenu les codes de lc2.
  "ville",
  "logement_floor",
  "logement_address_precision",
  // Informations pratiques du séjour, toutes présentes au livret.
  "parking_info",
  "parking_info_long",
  "poubelles_info",
  "infos_poubelles_long",
  "infos_courses_stockage",
  "regles_maison",
  // Descriptif de confort de la réservation (souvent absents des résas iCal).
  "guest_count",
  "guest_language",
  // Habillage de la conciergerie : nom (repli FBZ_DEFAULT_NAME) et logo.
  "fbz_name",
  "fbz_logo_url",
  // Phrase de notation dépréciée (texte figé, jamais vide en pratique).
  "rating_phrase",
]);

const _ESSENTIELLES = new Set(VARIABLES_ESSENTIELLES);
const _SECONDAIRES = new Set(VARIABLES_SECONDAIRES);

/**
 * Une variable vide a-t-elle le droit de retenir l'envoi ?
 * Inconnue des deux listes ⇒ oui (fail-closed).
 */
function estVariableEssentielle(key) {
  if (_ESSENTIELLES.has(key)) return true;
  if (_SECONDAIRES.has(key)) return false;
  return true;
}

/**
 * Sépare la liste rendue par `findMissingVars` en deux : celles qui retiennent
 * l'envoi, celles qui s'effacent. L'ordre d'origine est conservé.
 *
 * @param {string[]} missing
 * @returns {{ essentielles: string[], secondaires: string[] }}
 */
function classerVariablesManquantes(missing) {
  const essentielles = [];
  const secondaires = [];
  for (const key of missing || []) {
    (estVariableEssentielle(key) ? essentielles : secondaires).push(key);
  }
  return { essentielles, secondaires };
}

// Marque interne posée à la place de chaque token effacé, le temps de nettoyer
// autour (ligne vide, virgule orpheline, double espace). Caractère de la zone
// à usage privé Unicode : n'apparaît dans aucun gabarit.
const _MARQUE = "";
// Une ligne qui, tokens effacés, ne contient plus que blancs, ponctuation et
// symboles (émoji, tirets, puces) : aucune lettre ni chiffre → elle disparaît.
const _PONCTUATION_SEULE_RE = /^[\s\p{P}\p{S}\p{M}]*$/u;

function _echapperRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Retire du gabarit les tokens `{key}` des variables SECONDAIRES vides, et
 * nettoie ce que leur retrait laisse d'évident. N'agit que sur ces tokens ; ne
 * touche jamais un bloc `{#if key}` (la condition, elle, masque son bloc via
 * evaluateConditions). Liste vide ⇒ rend `template` tel quel (byte-identique).
 *
 * @param {string} template — gabarit AVANT rendu
 * @param {string[]} keys — variables secondaires vides (cf. classer…)
 * @returns {string}
 */
function effacerVariablesSecondaires(template, keys) {
  if (typeof template !== "string" || !keys || keys.length === 0) {
    return template;
  }
  // La liste fermée fait foi, pas l'appelant : une essentielle passée ici par
  // erreur (ou une inconnue) n'est JAMAIS effacée — un lien ou un code ne
  // disparaît pas d'un message parce qu'un appel s'est trompé de liste.
  const secondaires = keys.filter((k) => !estVariableEssentielle(k));
  if (secondaires.length === 0) return template;
  const tokens = new RegExp(
    `\\{(?:${secondaires.map(_echapperRegex).join("|")})\\}`,
    "g",
  );
  if (!tokens.test(template)) return template;
  tokens.lastIndex = 0;

  const marque = template.replace(tokens, _MARQUE);
  const lignes = marque.split("\n");
  const gardees = [];
  for (const ligne of lignes) {
    if (!ligne.includes(_MARQUE)) {
      gardees.push(ligne);
      continue;
    }
    // Ligne faite uniquement de tokens effacés (+ ponctuation) → disparaît.
    if (_PONCTUATION_SEULE_RE.test(ligne.split(_MARQUE).join(""))) continue;
    gardees.push(
      ligne
        // « a, {v}, b » → « a, b »
        .replace(new RegExp(`,\\s*${_MARQUE}\\s*,`, "g"), ",")
        // « a, {v} » (fin de ligne) → « a »
        .replace(new RegExp(`\\s*,\\s*${_MARQUE}\\s*$`), "")
        // « {v}, a » (début de ligne) → « a »
        .replace(new RegExp(`^\\s*${_MARQUE}\\s*,\\s*`), "")
        // « à {v} 🌸 » → « à 🌸 » (un seul espace, pas deux ; l'espace
        // insécable compte comme un blanc)
        .replace(new RegExp(`[ \\t\\u00A0]+${_MARQUE}[ \\t\\u00A0]+`, "g"), " ")
        // « à {v}. » → « à. » (pas d'espace avant le point)
        .replace(new RegExp(`[ \\t\\u00A0]+${_MARQUE}(?=[.;:!?)])`, "g"), "")
        // reste : le token seul s'efface
        .split(_MARQUE)
        .join("")
        // « Ville : {v} » → « Ville : » sans espace traînant
        .replace(/[ \t\u00A0]+$/, ""),
    );
  }
  return gardees.join("\n");
}

module.exports = {
  VARIABLES_ESSENTIELLES,
  VARIABLES_SECONDAIRES,
  estVariableEssentielle,
  classerVariablesManquantes,
  effacerVariablesSecondaires,
};
